import {
  type CurrentUser,
  type LayoutPermission,
  PermissionAdminLevel,
  PermissionLevel,
} from '@/api/User/types.ts'
import type { LayoutType } from '@/api/types.ts'
import {
  AdminPermission,
  type AdminPermissionType,
  InitiativePermission,
  type Role,
} from '@/hooks/useGetCurrentUser/types.ts'

export function hasAdminPermission(
  user: CurrentUser,
  type: AdminPermissionType,
  level: PermissionAdminLevel
) {
  if (level === PermissionAdminLevel.NONE)
    throw new Error('NONE não é permitido')
  const permissionLevel = user.superAdmin[type]
  const permissionsRanking = Object.values(PermissionAdminLevel)
  return (
    permissionsRanking.indexOf(permissionLevel) <=
    permissionsRanking.indexOf(level)
  )
}

export function hasPermission(
  user: CurrentUser,
  level: PermissionLevel,
  initiativeId?: number,
  featureId?: number,
  type: LayoutType | 'any' = 'any'
): boolean {
  let checkHasAdminPermission = false
  switch (level) {
    case PermissionLevel.ADMIN:
      checkHasAdminPermission = hasAdminPermission(
        user,
        'initiative',
        PermissionAdminLevel.ADMIN
      )
      break
    case PermissionLevel.WRITE:
      checkHasAdminPermission = hasAdminPermission(
        user,
        'initiative',
        PermissionAdminLevel.WRITE
      )
      break
    case PermissionLevel.READ:
    case PermissionLevel.COLLECTOR:
    case PermissionLevel.LOOKUP:
      checkHasAdminPermission = hasAdminPermission(
        user,
        'initiative',
        PermissionAdminLevel.READ
      )
      break
    case PermissionLevel.NONE:
      throw new Error('NONE não é permitido')
  }
  if (checkHasAdminPermission) return true

  if (initiativeId == null) {
    if (Object.keys(user.initiatives).length === 0) {
      return false
    }
    return Object.keys(user.initiatives).some((key) =>
      hasPermission(user, level, Number(key))
    )
  }

  const initiativePermission = user.initiatives[initiativeId]

  if (initiativePermission == null) return false
  if (initiativePermission.level === PermissionLevel.ADMIN) return true
  if (featureId == null) return false

  const featurePermission = initiativePermission.features[featureId]
  if (featurePermission == null) return false

  if (type === 'web' || type === 'mobile') {
    const permissionLevel = featurePermission[type]?.level
    if (permissionLevel == null) return false

    const permissionsRanking = Object.values(PermissionLevel)
    return (
      permissionsRanking.indexOf(permissionLevel) <=
      permissionsRanking.indexOf(level)
    )
  } else {
    return Object.values(featurePermission).some(
      (permission: LayoutPermission) => {
        const permissionsRanking = Object.values(PermissionLevel)
        return (
          permissionsRanking.indexOf(permission.level) <=
          permissionsRanking.indexOf(level)
        )
      }
    )
  }
}

export function someRole(user: CurrentUser, roles: Role[]) {
  if (roles.length === 0) return true
  return roles.some((role) => {
    if (role instanceof AdminPermission) {
      return hasAdminPermission(user, role.type, role.level)
    } else {
      return hasPermission(
        user,
        role.level,
        role.initiativeId,
        role.featureId,
        role.type
      )
    }
  })
}

export function adminPermission(
  type: AdminPermissionType,
  level: PermissionAdminLevel
): AdminPermission {
  return new AdminPermission(type, level)
}

export function initiativePermission(
  level: PermissionLevel,
  initiativeId?: number,
  featureId?: number,
  type: LayoutType | 'any' = 'any'
): InitiativePermission {
  return new InitiativePermission(level, initiativeId, featureId, type)
}
